import { useCallback, useEffect, useState } from "react";

import type { ConfigDTO } from "../../common/types/ConfigDTO";
import type { UserDTO } from "../../common/types/UserDTO";
import { useGetUserQuery } from "../store/services/user";
import { navigationChannel, VENUE_CHANGED } from "../utils/broadcastChannel";
import { getOrigin } from "../utils/url";

// When loaded venue is changed in Identity Management iFrame
// Trigger a reload of the current user record to then pass the correct venueId to other iFrame apps
export const useVenueSelectionListener = (user?: UserDTO, config?: ConfigDTO) => {
  const userId = user?.id;

  const [triggerRefetch, setTriggerRefetch] = useState(false);
  const { refetch } = useGetUserQuery(
    {
      // TODO Please correct typings soon
      // @ts-ignore
      id: userId,
    },
    { skip: !userId },
  );

  // Identity Management is the only sender of VENUE_CHANGED, so require its exact
  // origin rather than the broader isDexibitApi allow-list used for other messages.
  const aiakosOrigin = getOrigin(config?.aiakosUri);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data === VENUE_CHANGED) {
      setTriggerRefetch(true);
    }
  }, []);

  // BroadcastChannel only carries messages within one origin. Once nav and Identity
  // Management are on different hosts it delivers nothing, so accept the postMessage
  // fallback too. Both paths land on the same handler, and the refetch is idempotent,
  // so a duplicate delivery while they are same-origin is harmless.
  const handleWindowMessage = useCallback(
    (event: MessageEvent) => {
      if (!aiakosOrigin || event.origin !== aiakosOrigin) return;
      handleMessage(event);
    },
    [aiakosOrigin, handleMessage],
  );

  useEffect(() => {
    navigationChannel.addEventListener("message", handleMessage);
    window.addEventListener("message", handleWindowMessage);

    return () => {
      navigationChannel.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleWindowMessage);
    };
  }, [handleMessage, handleWindowMessage]);

  const refetchUser = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (triggerRefetch) {
      refetchUser();
      setTriggerRefetch(false);
    }
  }, [triggerRefetch, refetchUser]);
};
